import React, {
  useState,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store/ducks';
import store from 'store';

import Text from 'components/Text';
import { Row, Column } from 'components/Layout/Grid';
import { Button } from 'components/Button';
import Table from 'components/Table';
import StatusCircle from 'components/StatusCircle';
import AddNewUser from 'components/UserCustomerManagerSlider/AddNewUser';
import EditUser from 'components/UserCustomerManagerSlider/EditUser';
import toast from 'components/Toast/dispatch';
import { Visibility } from 'components/Visibility';

import keyIcon from 'img/green-key.svg';
import editIcon from 'img/edit-icon-table.svg';
import IconSearch from 'img/search-icon.svg';

import { IBatchRequestResponse } from 'models/Batch';
import { IUser, UserRoleTypes, UserType } from 'models/User';
import { ICustomerRole } from 'models/CustomerRole';

import UserService from 'services/user.service';

import { SelectCustomerIdIsArray } from 'utils/SelectCustomerIdIsArray';
import { UserFilter } from 'models/UserFilter';
import Form from 'components/Form';

const CustomerManager: React.FC = () => {
  const user = useSelector((state: RootState) => state.user);
  const { customer } = store.getState();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const randomTableSearchId = `${Math.floor(Math.random() * 1000)}`;

  const [users, setUsers] = useState<IBatchRequestResponse<IUser>>();

  const [tableOrderby, setTableOrderby] = useState('fullName');
  const [tableFilter, setTableFilter] = useState<UserFilter>();

  const [filterInput, setFilterInput] = useState('');
  const [statusInput, setStatusInput] = useState<string | undefined>('0');

  const [componentAlreadyMount, setComponentAlreadyMount] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const pageTotal = 10;

  const _userService = new UserService();

  async function ConfirmSend(email?: string) {
    if (email !== undefined) {
      toast.yesNo(
        `Deseja realmente enviar uma redefinição de senha para o usuário ${email}?`,
        () => userRequestNewPassword(email),
        'Sim',
        'Não'
      );
    } else toast.error('não encontrado e-mail');
  }

  async function userRequestNewPassword(email: string) {
    await _userService
      .resetPassword(email)
      .then(() => {
        toast.success('E-mail enviado com sucesso!');
      })
      .catch(() => {
        toast.error('Ocorreu um erro inesperado.');
      });
  }

  const processOrdination = () => {
    if (!tableOrderby) return undefined;

    try
    {
      const parts = tableOrderby.split(' ');
      const field = parts[0];
      const directionText = parts[1];
      const direction = directionText === 'desc' ? 1 : 0;

      if (field === 'enabled')
        return { field: 0, direction };
      if (field === 'fullName')
        return { field: 1, direction };

      return undefined;
    }
    catch(e) {
      console.error(e);
      return undefined;
    }
  };

  const getUsers = useCallback(async () => {
    setUsers(undefined);

    const ordination = processOrdination();

    await new UserService()
      .filter(currentPage, pageTotal, tableFilter, ordination)
      .then((result: IBatchRequestResponse<IUser> | undefined) => {
        if (result) {
          const { data, totalPages, pageSize, total, error } = result;
          setUsers({ data, totalPages, pageSize, total, error });
        }
      });
  }, [tableFilter, tableOrderby, currentPage]);

  const CustomerTable = useMemo(
    () => (
      <Table
        trackingKey="Grid Usuários Clientes"
        className="mt-3"
        setTableOrder={setTableOrderby}
        tableOrder={tableOrderby}
        pageSize={pageTotal}
        totalPages={users?.totalPages}
        onReload={getUsers}
        error={users?.error}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        data={users?.data}
        model={[
          {
            label: 'Status',
            key: 'enabled',
            size: 80,
            maxSize: 80,
            Component: (data: IUser) => (
              <Row className="justify-center w-100">
                <StatusCircle value={status(data)} pending={data?.pending ?? false} />
              </Row>
            ),
          },
          { label: 'Nome', key: 'fullName', size: 200 },
          { label: 'E-mail', key: 'email', size: 240 },
          { label: 'Telefone', key: 'phoneNumber', type: 'phone', size: 130 },
          {
            label: 'Nível de acesso',
            key: 'acessLevel',
            size: 120,
            maxSize: 120,
            Component: (data: IUser) => (
              <Text className="color-blue-gray" size={12} showTip>
                {data.roles ? UserRoleTypes?.[data.roles[0]] : '-'}
              </Text>
            ),
          },
          {
            label: 'Adicionado por',
            key: 'userWhoInvited.fullName',
            size: 110,
            maxSize: 110,
            Component: (data: IUser) => (
              <Text className="color-blue-gray" size={12} showTip>
                {data.userWhoInvited?.fullName === user?.fullName
                  ? 'Você'
                  : data.userWhoInvited?.fullName || '-'}
              </Text>
            ),
          },
          {
            label: 'Último acesso',
            key: 'lastAccess',
            type: 'date',
            size: 100,
            maxSize: 100,
          },
          { label: 'Expira', key: 'expires', size: 90 },
          {
            label: '',
            size: 40,
            maxSize: 40,
            isButton: true,
            Component: (userComp: IUser) => (
              <Button
                hide={userComp.fullName === undefined}
                tooltip="Trocar senha"
                onClick={() => ConfirmSend(userComp.email)}
              >
                <img src={keyIcon} alt="" />
              </Button>
            ),
          },
          {
            label: '',
            size: 40,
            maxSize: 40,
            isButton: true,
            Component: (userComp: IUser) => (
              <Visibility visible={!userComp.pending}>
                <Button
                  tooltip="Editar usuário"
                  onClick={() => {
                    !SelectCustomerIdIsArray() &&
                      dispatch({
                        type: 'OPEN_SLIDER',
                        data: {
                          title: 'Editar Usuário',
                          subtitle:
                            'Utilize os campos abaixo para alterar informações do usuário.',
                          content: EditUser,
                          state: {
                            ...userComp,
                            currentPage,
                            setCurrentPage,
                            getUsers,
                          },
                        },
                      });
                  }}
                >
                  <img src={editIcon} alt="" />
                </Button>
              </Visibility>
            ),
          },
        ]}
      />
    ),
    [users, user?.selectedCustomerId]
  );

  const makeFilterStatus = () => {
    if (statusInput === 'active')
      return true;

    return statusInput === 'disabled'
      ? false
      : undefined;
  };

  function makeFilter() {
    setCurrentPage(0);

    const statusConst = makeFilterStatus();
    let data: UserFilter = {
      search: '',
      userType: UserType.Customer,
      profileType: undefined,
      deleted: false
    };

    if (filterInput !== '') {
      data.search = filterInput;
    }

    if (statusConst !== undefined) {
      data.enabled = statusConst;
    }

    if (statusInput === 'pending') {
      data.pending = true;
    }

    data.customerId = customer?.id || user?.customerId || user?.selectedCustomerId; 

    setCurrentPage(0);
    setTableFilter(data);
  }

  function status(data: IUser) {
    let value = false;
    if (user?.userType === 'employee') {
      value =
        (data?.enabled &&
          data?.customersRoles?.find((c) => c.customerId === customer?.id)
            ?.enabled) ||
        false;
    } else {
      value =
        (data?.enabled &&
          data?.customersRoles?.find(
            (c: ICustomerRole) => user?.selectedCustomerId?.includes(c.customerId!)
          )?.enabled) ||
        false;
    }
    statusInput !== '0' && setStatusInput(value ? 'active' : 'disabled');
    return value;
  }

  useEffect(() => {
    if (!componentAlreadyMount) return setComponentAlreadyMount(true);
    getUsers();
    return;
  }, [getUsers]);

  useEffect(() => {
    makeFilter();
    if (
      user?.userType === 'employee' ||
      (user?.userType === 'customer' && user.roles?.[0] === 'administrator')
    )
      return;
    navigate('/dashboard');
  }, [user?.selectedCustomerId]);

  return (
    <>
      <Row className="align-center justify-between flex-lg-column align-lg-none">
        <Text className="color-primary font-bold" size={24}>
          Gerenciar usuários
        </Text>
      </Row>
      <Form id="table-search-customer-manager" onValidate={makeFilter}>
        <Row
          id={randomTableSearchId}
          className="align-center mt-3 bg-white px-3 py-3 align-lg-none flex-lg-column"
          style={{ borderRadius: '10px 10px 0px 0px' }}
        >
          <Column>
            <Text size={12} className="mb-2 color-blue-gray mb-lg-2">
              Filtro
            </Text>
            <input
              onChange={(e) => setFilterInput(e.target.value)}
              type="email"
              className="w-lg-100"
              placeholder="Usuário ou e-mail"
              style={{ width: 390 }}
            />
          </Column>
          <Column className="ml-2 mt-lg-2 ml-lg-0">
            <Text size={12} className="mb-2 color-blue-gray mb-lg-2">
              Status
            </Text>
            <select
              name="status"
              defaultValue={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
              className="w-lg-100"
              style={{ minWidth: 210 }}
            >
              <option value="0">Todos</option>
              <option value="active">Ativo</option>
              <option value="pending">Pendente</option>
              <option value="disabled">Inativo</option>
            </select>
          </Column>
          <Column className="mr-2 mr-lg-0 mt-md-3 w-md-100 ml-2 ml-md-0 pt-4 pt-md-0">
            <Row>
              <Button
                tabIndex={0}
                className="focus-button"
                width="auto"
                onClick={makeFilter}
              >
                <img src={IconSearch} alt="" />
              </Button>
            </Row>
          </Column>
          <Row className="align-center mt-2 flex-1 justify-end flex-lg-column">
            <Button
              className="outline mt-2 ml-lg-0 w-lg-100"
              onClick={() => {
                !SelectCustomerIdIsArray() &&
                  dispatch({
                    type: 'OPEN_SLIDER',
                    data: {
                      title: 'Adicionar Novo Usuário',
                      subtitle:
                        'Preencha os campos abaixo para enviar um convite de acesso a um novo usuário. O usuário receberá por e-mail um link para validação do acesso.',
                      content: AddNewUser,
                      state: { ...user, currentPage, setCurrentPage, getUsers },
                    },
                  });
              }}
              width="auto"
            >
              <Text className="color-primary font-bold px-1">
                Convidar Usuário
              </Text>
            </Button>
          </Row>
        </Row>
      </Form>
      {CustomerTable}
    </>
  );
};

export default CustomerManager;
